import type { SingleOrRange } from 'components/shared/DateNavigation/DateNavigation.types';
import {
  addDays,
  addWeeks,
  addHours,
  differenceInMinutes,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  Month,
  parseISO,
  set,
  setDay,
  startOfDay,
  startOfMonth,
  startOfToday,
  startOfWeek,
  startOfYear
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import dayjs, { Dayjs } from 'dayjs';
import { enUS, nl } from 'date-fns/locale';

import { DateString, StrDigit } from 'types';

import { getStore } from 'utils/reduxStore';

const locales = {
  en: enUS,
  nl
};

locales.en.options = { weekStartsOn: 1, firstWeekContainsDate: 4 };
locales.nl.options = { weekStartsOn: 1, firstWeekContainsDate: 4 };

/** Matches TenantService.getOrganisationTimezone() default. */
const DEFAULT_ORGANISATION_TIMEZONE = 'Europe/Amsterdam';

let cachedHoursDiff: number | undefined;
let cachedTimezone: string | undefined;

/** Clears the cached org/browser offset. Exported for tests. */
export const resetHoursDiffCache = () => {
  cachedHoursDiff = undefined;
  cachedTimezone = undefined;
};

const getOrganisationTimezone = () => {
  const { organisationPreferences } = getStore().getState();
  return organisationPreferences?.timezone || DEFAULT_ORGANISATION_TIMEZONE;
};

/**
 * Hours between the browser's local zone and the organisation timezone.
 * Cached per timezone so a late-loading org preference can still correct the value.
 * Falls back to Europe/Amsterdam when prefs are not loaded yet (avoids treating
 * timezone as UTC and shifting times by ~2h on refresh races).
 */
const getHoursDiff = () => {
  const timezone = getOrganisationTimezone();

  if (cachedHoursDiff !== undefined && cachedTimezone === timezone) {
    return cachedHoursDiff;
  }

  const currentDate = new Date();
  cachedHoursDiff =
    (+currentDate - +toZonedTime(currentDate, timezone)) / 3600000;
  cachedTimezone = timezone;
  return cachedHoursDiff;
};

export const toNativeDate = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return value;
  }

  if (
    value !== null &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof value.toDate === 'function'
  ) {
    return value.toDate();
  }

  return null;
};

// check whether the value is a single or range date
export function flattenToDate(value: SingleOrRange): Date | null {
  const first = Array.isArray(value) ? value?.[0] ?? null : value;
  return toNativeDate(first);
}

export const getLocale = () => {
  const locale = navigator.language.split('-')[0];
  return locale === 'en' || locale === 'nl' ? locale : 'nl';
};
export const getDateFnsLocale = () => locales[getLocale()];
export const formatByLocale = (date: Date, pattern = 'P') =>
  format(date, pattern, { locale: getDateFnsLocale() });

export const formatDate = (date: Dayjs | string | undefined) =>
  (date && dayjs(date).format('DD-MM-YYYY')) || '-';

export const convertToFormattedDate = (date: Date | string) =>
  dayjs(date).format('DD-MM-YYYY');

export const formatDateTime = (
  date: Dayjs | string | undefined,
  includeSeconds = false
) =>
  (date &&
    dayjs(date).format(`DD-MM-YYYY HH:mm${includeSeconds ? ':ss' : ''}`)) ||
  '-';

export const getStartOfDay = (date: Date | string) =>
  startOfDay(new Date(date));

export const getEndOfDay = (date: Date | string) => endOfDay(new Date(date));

export const toLocalISOString = (date: Date) =>
  dayjs(date).add(getHoursDiff(), 'hour').toISOString();

export const toLocalDate = (date: DateString) =>
  set(new Date(date), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });

export const fromISOString = (date: string | Date) =>
  addHours(new Date(date), -getHoursDiff());

/** yyyy-MM-dd: the plain date the API endpoints take as a date parameter */
export const toDateString = (date: Date) =>
  format(date, 'yyyy-MM-dd') as DateString;

/**
 * Same, for an endpoint parameter that is only filled in once the user picked a date.
 *
 * The widening is the point, and both halves of it have to stay: `DateString` is a union
 * of every yyyy-MM-dd pattern, and unioning that with `''` in a ternary is more than
 * TypeScript will represent -- it gives up with TS2590. The `as string` keeps the ternary
 * itself representable, the annotated return type keeps the union out of every call site.
 * The endpoints take a plain string anyway.
 */
export const toOptionalDateString = (date: Date | null | undefined): string =>
  date ? (toDateString(date) as string) : '';

/**
 * The backend parses datetimes as real UTC (JsonDateTimeFormat pins the
 * timezone to UTC), so the local day boundary has to be sent as the instant it
 * actually is: 00:00 in Amsterdam becomes 22:00Z the day before. Formatting the
 * local wall clock with a Z suffix would shift every absence by the UTC offset.
 */
export const toBackendDateTime = (date: Date, endOfDay?: boolean) => {
  const dayStart = startOfDay(date);

  return (
    endOfDay ? set(dayStart, { hours: 23, minutes: 59 }) : dayStart
  ).toISOString();
};

/** The backend serialises datetimes in real UTC (JsonDateTimeFormat), so they
 *  are parsed as such and shown in local time: 07:00Z reads as 09:00. */
export const parseBackendDateTime = (value: string) => parseISO(value);

/** Ensures a datetime string carries the Z suffix the backend's date pattern
 *  (yyyy-MM-dd'T'HH:mm:ss.SSS'Z') requires. */
export const suffixZ = (value: string) =>
  value.endsWith('Z') ? value : `${value}Z`;

export const formatISOString = (date: string, pattern = 'P') =>
  formatByLocale(fromISOString(date), pattern);

const MAX_DATE = '2099-01-01';
const MIN_DATE = '1901-01-01';
export const getMinDate = () => new Date(MIN_DATE);
export const getMaxDate = () => new Date(MAX_DATE);

const isDSTObserved = (date: Date) => {
  const jan = new Date(date.getFullYear(), 0, 1);
  const jul = new Date(date.getFullYear(), 6, 1);
  return (
    date.getTimezoneOffset() <
    Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset())
  );
};

export const getDSTDiff = (date: Date) => {
  const todayDST = isDSTObserved(date);
  const tomorrowDST = isDSTObserved(addDays(date, 1));
  return todayDST !== tomorrowDST ? (todayDST ? -1 : 1) : 0;
};

export enum WeekDays {
  SUNDAY,
  MONDAY,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURDAY
}

export type WeekDay = Lowercase<keyof typeof WeekDays>;

export type Quarter = 1 | 2 | 3 | 4;

export const getWeekDates = () => [
  setDay(startOfToday(), 1),
  setDay(startOfToday(), 2),
  setDay(startOfToday(), 3),
  setDay(startOfToday(), 4),
  setDay(startOfToday(), 5),
  setDay(startOfToday(), 6),
  setDay(startOfToday(), 0)
];

export const getWeekInterval = (date?: Date | null) => ({
  startDate: startOfWeek(date || new Date(), { weekStartsOn: 1 }),
  endDate: endOfWeek(date || new Date(), { weekStartsOn: 1 })
});

export const getFourWeekInterval = (date?: Date | null) => ({
  startDate: startOfWeek(date || new Date(), { weekStartsOn: 1 }),
  endDate: endOfWeek(addWeeks(date || new Date(), 3), { weekStartsOn: 1 }) // 3 weeks + endOfWeek
});

export const getEightWeekInterval = (date?: Date | null) => ({
  startDate: startOfWeek(date || new Date(), { weekStartsOn: 1 }),
  endDate: endOfWeek(addWeeks(date || new Date(), 7), { weekStartsOn: 1 }) // 7 weeks + endOfWeek
});

export const getWorkweekInterval = (date?: Date | null) => ({
  startDate: startOfWeek(date || new Date(), { weekStartsOn: 1 }),
  endDate: dayjs(endOfWeek(date || new Date(), { weekStartsOn: 1 }))
    .subtract(2, 'days')
    .toDate()
});

export const getMonthInterval = (date?: Date | null) => ({
  startDate: startOfMonth(date || new Date()),
  endDate: endOfMonth(date || new Date())
});

export const getYearInterval = (date?: Date | null) => ({
  startDate: startOfYear(date || new Date()),
  endDate: endOfYear(date || new Date())
});

export type TimePeriod = `${StrDigit}${StrDigit}:${StrDigit}${StrDigit}`;

export const getDiffInMinutes = (endTime: string, startTime: string) =>
  differenceInMinutes(fromISOString(endTime), fromISOString(startTime));

export const formatMinutesInHours = (
  durationInMinutes: number,
  withTrailingZero?: boolean
) => {
  const totalHours = durationInMinutes / 60;
  const intHours = Math.trunc(totalHours);
  const hours = Math.abs(intHours);
  const minutes = Math.round(Math.abs((totalHours - intHours) * 60));
  const isEn = getLocale() === 'en';
  const h = isEn ? 'h' : 'u';
  // Derived once and applied in every branch below - a whole-hour negative
  // duration (e.g. -600) has no minute remainder, so the old code that only
  // prepended the sign in the `minutes` branch silently dropped it.
  const sign = durationInMinutes < 0 ? '-' : '';

  return minutes
    ? `${sign}${hours}${h}${String(minutes).padStart(2, '0')}${isEn ? 'm' : ''}`
    : withTrailingZero
    ? `${sign}${hours}${h}00${isEn ? 'm' : ''}`
    : `${sign}${hours}${h}`;
};

export const getHoursMinutes = (date: Date | string) =>
  format(typeof date === 'string' ? fromISOString(date) : date, 'HH:mm');
export const getHoursMinutesSeconds = (date: Date | string) =>
  format(typeof date === 'string' ? fromISOString(date) : date, 'HH:mm:ss');

export const formatHoursMinutes = (durationInSeconds: number) => {
  const negative = durationInSeconds < 0 ? `-` : ``;

  const durationInSecondsAbs = Math.abs(durationInSeconds);
  const hours = Math.floor(durationInSecondsAbs / 3600);
  const minutes = Math.round(durationInSecondsAbs / 60 - hours * 60);
  const result =
    hours != 0
      ? `${hours}u${minutes > 9 ? minutes : `0${minutes}`}`
      : `${minutes}min`;
  return `${negative}${result}`;
};

export const formattedHoursMinutesToSeconds = (value: string) => {
  if (value === null || value === undefined) return NaN;

  if (value.match(/^\d*$/gi)) {
    return +value * 3600; // We consider pure numbers as hours
  } else if (value.match(/^\d{1,2}min$/gi)) {
    const [minutes = 0] = value.split('min');
    if (+minutes > 60) return NaN;
    return +value.split('min')[0] * 60;
  } else if (value.match(/^\d{1,2}u\d{2}/gi)) {
    const [hours = '00', minutes = '00'] = value.split('u');
    if (+hours > 24 || +minutes > 60) return NaN;
    return +hours * 3600 + +minutes * 60;
  }

  return NaN;
};

export const formatHours = (durationInSeconds: number) => {
  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.round(durationInSeconds / 60 - hours * 60);
  return `${hours > 9 ? hours : `0${hours}`}u${
    minutes > 9 ? minutes : `0${minutes}`
  }`;
};

export const getLocalizedMonthNames = (
  width: 'narrow' | 'abbreviated' | 'wide' = 'wide'
): string[] => {
  const locale = getDateFnsLocale();
  return Array(12)
    .fill('')
    .map((v, i) => locale?.localize?.month(i as Month, { width }) || '');
};

export const getLocalizedMonthName = (
  month: Month,
  width: 'narrow' | 'abbreviated' | 'wide' = 'wide'
): string => {
  const locale = getDateFnsLocale();
  return locale?.localize?.month(month, { width }) || '';
};

export const getStartDateFromWeekNumber = (week: number, year: number) => {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const isoWeekStart = simple;

  if (dow <= 4) {
    isoWeekStart.setDate(simple.getDate() - simple.getDay() + 1);
  } else {
    isoWeekStart.setDate(simple.getDate() + 8 - simple.getDay());
  }

  return isoWeekStart;
};

export const getHoursAndMinutesFromSeconds = (
  seconds: number
): { h: number; m: number } => {
  if (!seconds) return { h: 0, m: 0 };

  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return { h: hours, m: minutes };
};

/**
 * Decimal hours → whole hours and minutes. A rounded-up 60 minutes carries
 * into the next hour, so 3,999 hours reads as 4 hours and never as
 * "3 hours 60 minutes".
 */
export const getHoursAndMinutesFromHours = (
  hours: number
): { h: number; m: number } => {
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);

  return minutes === 60
    ? { h: wholeHours + 1, m: 0 }
    : { h: wholeHours, m: minutes };
};

export const formatSecondsToHoursMinutes = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round(seconds / 60 - hours * 60);
  const hoursFormat =
    hours > 0 && hours <= 23
      ? `${hours}u `
      : minutes === 60
      ? `${hours + 1}u `
      : '';

  const minutesFormat = minutes && minutes !== 60 ? `${minutes}m` : '0m';

  return `${hoursFormat}${minutesFormat}`;
};

const DAY_OFFSET = {
  0: '  ',
  1: '⁺¹',
  2: '⁺²',
  3: '⁺³'
};

export const formatTime = (
  input: string | Date | Dayjs | null | undefined,
  baseDate?: Date | Dayjs
) => {
  if (!input) {
    return '-';
  }
  const date = dayjs(typeof input == 'string' ? fromISOString(input) : input);
  return date.format('HH:mm') + formatOffset(date, baseDate);
};

export const formatOffset = (
  input: string | Date | Dayjs | null | undefined,
  baseDate: Date | Dayjs | null | undefined = undefined
) => {
  if (!input || !baseDate) {
    return '';
  }
  const date = dayjs(typeof input == 'string' ? fromISOString(input) : input);
  const timeStart = dayjs(
    baseDate || fromISOString('1970-01-01T00:00:00+01:00')
  );
  const dayOffset = date.diff(timeStart, 'days');
  return DAY_OFFSET[dayOffset] || '⁺⁺';
};

export const converterForDate = (date: Date) =>
  dayjs(date).format('MMM D, YYYY');

export const converterForDateTime = (date: Date) =>
  dayjs(date).format('MMM D, YYYY HH:mm:ss A');

export const converterForDateAndTime = (date: Date | string) =>
  dayjs(date).format('D-MM-YYYY HH:mm');

export const shouldDisableDateInRange = (
  date: Date | Dayjs,
  validFrom?: Date | Dayjs | null | undefined,
  validTo?: Date | Dayjs | null | undefined
): boolean => {
  return (
    dayjs(validFrom || NaN).isAfter(date, 'day') ||
    dayjs(validTo || NaN).isBefore(date, 'day')
  );
};

export const shouldDisableDateAfterYears =
  (years: number) =>
  (date: Date | Dayjs): boolean => {
    const maxDate = dayjs().endOf('year').add(years, 'year');
    return dayjs(date).isAfter(maxDate, 'day');
  };

export const shouldDisableDateAfterFirstOfMonth = (date: Date | Dayjs) => {
  if (!date) return false;
  const dayjsDate = dayjs(date);
  return dayjsDate.date() !== 1;
};

export const TIME_RANGE_BASE_DATE = dayjs('1970-01-01').startOf('day');

export type TimeRange = {
  timeFrom: Dayjs;
  timeTo: Dayjs;
};

export const timeToBaseDate = (time: Dayjs, baseDate: Dayjs) => {
  return time
    .set('year', baseDate.get('year'))
    .set('month', baseDate.get('month'))
    .set('date', baseDate.get('date'));
};

// timeTo always falls in (timeFrom, timeFrom + 24h], so equal times mean a full day.
// The interval cannot express more than a day, so a stored range longer than 24h is
// shortened - that is the price of lowering timeFrom collapsing timeTo back onto the
// same day, which the pickers need. ponytail: revisit if >24h ranges turn up in data.
// Date and string are accepted because the yup resolver hands handleSubmit the
// casted values (yup.date()), not the Dayjs the picker produced. Both sides are
// required: silently substituting the base date for a missing time invented a
// midnight that no one entered.
export const normalizeTimeRange = (
  timeFrom: Dayjs | Date | string,
  timeTo: Dayjs | Date | string,
  baseDate: Dayjs = TIME_RANGE_BASE_DATE
): TimeRange => {
  const validTimeFrom = dayjs(timeFrom);
  const validTimeTo = dayjs(timeTo);

  const normalizedTimeFrom = timeToBaseDate(validTimeFrom, baseDate);
  const sameDayTimeTo = timeToBaseDate(validTimeTo, baseDate);

  return {
    timeFrom: normalizedTimeFrom,
    timeTo: sameDayTimeTo.isAfter(normalizedTimeFrom)
      ? sameDayTimeTo
      : sameDayTimeTo.add(1, 'day')
  };
};
